/**
 * In-app help content — sections/topics data plus the `pathname → topic` lookup.
 *
 * This is a plain data module (no framework code) so it stays easy to extend as
 * new use cases ship: `maintain-in-app-help-docs` adds/updates at most one
 * section or topic per activity (never a full rewrite) — see the file header
 * comment on each topic for which activity/use case it documents.
 *
 * Shape:
 *   helpSections: [{ id, label, icon, topics: [{ id, label, title, body }] }]
 *   body is a short HTML string rendered via dangerouslySetInnerHTML.
 */

export const helpSections = [
  {
    id: 'welcome',
    label: 'Welcome',
    icon: 'fa fa-compass',
    topics: [
      {
        id: 'getting-started',
        label: 'Getting Started',
        title: 'Welcome to PropStudioOS',
        // Bootstrapped by the Users, Groups & Roles console activity (UC 56c53412).
        body: `
          <p>PropStudioOS is the multi-tenant workspace platform for your organization's
          creative tooling. Every workspace is an isolated tenant with its own subdomain, member
          roster, and access rules, so different teams and clients can share one platform without
          seeing each other's projects.</p>
          <p style="margin-top:8px;">Sign in from the <strong>Sign-in</strong> screen with your
          corporate credentials or your organization's SSO provider. Once signed in you land on the
          <strong>App Gallery</strong> — your workspace's home, with quick-launch cards for the
          creative tools you have access to and a summary of your recent activity.</p>
          <p style="margin-top:12px;"><strong>Finding your way around:</strong></p>
          <ul>
            <li><strong>Top navigation bar</strong> — jump between the tools your role can access.</li>
            <li><strong>Account menu</strong> — your profile, workspace switcher, and log out, in the
              top-right corner.</li>
            <li><strong>Help</strong> — this drawer, opened from the <i class="fa fa-question-circle"
              aria-hidden="true"></i> button next to your account menu, any time you need it.</li>
          </ul>
          <p style="margin-top:8px; font-size:13px; color:#a4a29f;">Tip: what you see in the
          navigation and the App Gallery depends on your assigned platform role — a Platform
          Administrator sees the full administration console; other roles see only their tools.</p>
        `,
      },
    ],
  },
  {
    id: 'identity-access-workspaces',
    label: 'Identity & Access',
    icon: 'fa fa-shield-alt',
    topics: [
      {
        id: 'sign-in-sso',
        label: 'Signing In & SSO',
        title: 'Signing In',
        // Documents the Sign-in / SSO Redirect Screen (page 1cfc8a3e…), UC 56c53412.
        body: `
          <p>The <strong>Sign-in</strong> screen supports two ways into PropStudioOS:</p>
          <ol>
            <li><strong>Corporate credentials</strong> — enter your email and password. If your
              account requires multi-factor authentication you'll be prompted for a one-time code
              before you're signed in.</li>
            <li style="margin-top:8px;"><strong>Single sign-on (SSO)</strong> — choose your
              organization's identity provider button (SAML 2.0 or OIDC). You're redirected to your
              provider to authenticate, then returned here already signed in.</li>
          </ol>
          <p style="margin-top:12px;">If your organization belongs to more than one workspace, use
          the <strong>workspace picker</strong> to choose which tenant you're signing in to before
          submitting — this determines which projects and data you'll see.</p>
          <p style="margin-top:8px;"><strong>Forgot your password?</strong> Use the password reset
          link on the sign-in form to receive a reset email.</p>
          <div style="background:#f8f7ff; border:1px solid #e5e7eb; border-radius:8px; padding:12px; margin-top:16px;">
            <p style="color:#d4af37; font-weight:600; margin-bottom:8px;">For Platform Administrators</p>
            <p style="font-size:13px;">SSO sign-in only works once your identity provider is
            configured. Set it up from <strong>Users &amp; Roles → SSO / Identity Providers</strong>
            — see that topic in this section for the walkthrough.</p>
          </div>
        `,
      },
      {
        id: 'app-gallery',
        label: 'App Gallery (Home)',
        title: 'App Gallery — Your Workspace Home',
        // Documents the Home / App Gallery Screen (page b7703015…), UC 56c53412.
        body: `
          <p>The <strong>App Gallery</strong> is your landing page after signing in — the home base
          for your current workspace.</p>
          <p style="margin-top:12px;"><strong>What you'll find here:</strong></p>
          <ul>
            <li><strong>Quick-launch cards</strong> — one-click access to the creative tools your
              role can use (generation studio, model registry, review queue, and more).</li>
            <li><strong>Active projects summary</strong> — a snapshot of the projects you're
              currently working on.</li>
            <li><strong>Recent generations</strong> — a scrollable tray of your latest creative
              outputs, with a link to the full history.</li>
            <li><strong>Model status widgets</strong> — whether the underlying AI models your tools
              depend on are online and responding.</li>
            <li><strong>Usage quota</strong> — how much of your workspace's monthly generation quota
              you've used, so you can plan ahead.</li>
          </ul>
          <p style="margin-top:8px; font-size:13px; color:#a4a29f;">Tip: only cards for tools you're
          authorized to use appear here — if a tool you expect is missing, ask your Platform
          Administrator to check your assigned role.</p>
        `,
      },
      {
        id: 'users-groups-roles',
        label: 'Users, Groups & Roles',
        title: 'Managing Users, Groups & Roles',
        // Documents the Users, Groups & Roles console (page 767e5613…), UC 56c53412.
        // Platform Administrator only.
        body: `
          <p><strong>Users &amp; Roles</strong> (Platform Administrator only) is where you manage who
          can access your workspaces and what they can do. It has three tabs.</p>
          <p style="margin-top:12px;"><strong>Directory &amp; Roles:</strong></p>
          <ul>
            <li>Search and filter every user and service account by role, workspace, or status.</li>
            <li>Click <strong>Edit Roles</strong> on any row to open the assignment dialog, where you
              check the built-in platform roles (Creative User, Approver, Workflow Developer, Model
              Admin, Platform Admin, Auditor) and set per-workspace access (Owner, Editor, Viewer).</li>
            <li>Use <strong>Sync Entra Groups</strong> to force an immediate SSO group
              synchronization, or <strong>Create Service Account</strong> to issue credentials for an
              automated integration.</li>
          </ul>
          <p style="margin-top:12px;"><strong>Workspaces &amp; Isolation:</strong></p>
          <p>Lists every provisioned workspace with its custom subdomain, isolation boundary, data
          residency, and member/service-account counts. Use <strong>Provision Workspace</strong> to
          create a new isolated tenant.</p>
          <p style="margin-top:12px;"><strong>SSO / Identity Providers:</strong></p>
          <p>Connect a SAML 2.0 or OIDC identity provider — see the <strong>SSO / Identity
          Providers</strong> topic in this section for the step-by-step setup.</p>
        `,
      },
      {
        id: 'workspaces-isolation',
        label: 'Workspaces & Isolation',
        title: 'Provisioning Workspaces',
        // Documents the "Workspaces & Isolation" panel added to the Users, Groups &
        // Roles console beyond the raw design (story 9ffd3222 AC1), UC 56c53412.
        body: `
          <p>Each <strong>workspace</strong> is an isolated tenant boundary: its own subdomain, data
          residency setting, and member roster, kept separate from every other workspace on the
          platform. Use this when you need to give a new team, client, or business unit its own
          walled-off environment.</p>
          <p style="margin-top:12px;"><strong>To provision a new workspace:</strong></p>
          <ol>
            <li>Open <strong>Users &amp; Roles → Workspaces &amp; Isolation</strong>.</li>
            <li style="margin-top:8px;"><strong>Provision Workspace</strong> — click the button to
              open the creation dialog.</li>
            <li style="margin-top:8px;"><strong>Name and configure</strong> — set the workspace
              name, its custom subdomain, and the isolation boundary and data residency it should
              use.</li>
            <li style="margin-top:8px;"><strong>Confirm</strong> — the new workspace appears in the
              table immediately with a status badge, ready to receive members and an SSO
              configuration.</li>
          </ol>
          <p style="margin-top:8px; font-size:13px; color:#a4a29f;">Tip: the member and service
          account counts on each row update automatically as you assign users and roles from the
          Directory tab.</p>
        `,
      },
      {
        id: 'sso-identity-providers',
        label: 'SSO / Identity Providers',
        title: 'Configuring SSO / Identity Providers',
        // Documents the "SSO / Identity Providers" panel added to the Users, Groups &
        // Roles console beyond the raw design (story 9ffd3222 AC2), UC 56c53412.
        body: `
          <p>The <strong>SSO / Identity Providers</strong> tab connects your organization's identity
          provider so members can sign in with their corporate credentials instead of a
          platform-specific password.</p>
          <p style="margin-top:12px;"><strong>To connect a provider:</strong></p>
          <ol>
            <li>Choose the <strong>protocol</strong> — SAML 2.0 or OIDC — and give the provider a
              display name.</li>
            <li style="margin-top:8px;"><strong>SAML 2.0</strong> needs the Entity ID, SSO URL, and
              the X.509 signing certificate (paste the full PEM block). The Single Logout URL and
              NameID format are optional.</li>
            <li style="margin-top:8px;"><strong>OIDC</strong> needs the Issuer URL, Client ID, and
              Client Secret, plus the scopes to request.</li>
            <li style="margin-top:8px;"><strong>Auto-provisioning</strong> — enable it to create a
              directory record automatically the first time a user signs in via this provider, and
              map identity-provider group claims to platform roles so new users land with the right
              access from day one.</li>
            <li style="margin-top:8px;"><strong>Test Authentication</strong> — always run this before
              saving, to confirm the provider accepts a real assertion and resolves to the expected
              role.</li>
            <li style="margin-top:8px;"><strong>Save SSO Configuration</strong> — once the test
              passes, save to activate the provider for your workspace.</li>
          </ol>
          <p style="margin-top:8px; font-size:13px; color:#a4a29f;">Tip: choose whether a provider
          applies tenant-wide or to a single workspace using the "Applies To" field — useful when
          different business units use different identity providers.</p>
        `,
      },
    ],
  },
  {
    id: 'roles',
    label: 'Your role',
    icon: 'fa fa-id-badge',
    topics: [
      {
        id: 'who-can-do-what',
        label: 'Who can do what',
        title: 'What your role lets you do',
        // Added when the navigation rail began hiding entries by role: people
        // were asking why a colleague could see a screen they could not.
        body: `
          <p>The navigation only shows what your platform role can reach, so two people can be
          signed in to the same workspace and see different menus. That is expected — nothing is
          broken.</p>
          <table style="width:100%;margin-top:10px;border-collapse:collapse;font-size:13px;">
            <tr><th style="text-align:left;padding:4px 8px;">Role</th><th style="text-align:left;padding:4px 8px;">What it is for</th></tr>
            <tr><td style="padding:4px 8px;"><strong>Creative User</strong></td>
                <td style="padding:4px 8px;">Generate, iterate and submit work for review. Generation Studio,
                Property Bibles, Media Library, Asset Vault.</td></tr>
            <tr><td style="padding:4px 8px;"><strong>Approver</strong></td>
                <td style="padding:4px 8px;">Everything a Creative User can do, plus the Review Queue —
                approving, rejecting and requesting changes.</td></tr>
            <tr><td style="padding:4px 8px;"><strong>Workflow Developer</strong></td>
                <td style="padding:4px 8px;">Build and publish multi-step pipelines in the Workflow Editor.</td></tr>
            <tr><td style="padding:4px 8px;"><strong>Model Administrator</strong></td>
                <td style="padding:4px 8px;">Register models, edit capability schemas, set routing policies,
                and train new identities. These actions spend GPU time and money.</td></tr>
            <tr><td style="padding:4px 8px;"><strong>Auditor / Compliance</strong></td>
                <td style="padding:4px 8px;">Read the immutable audit log and export provenance reports.</td></tr>
            <tr><td style="padding:4px 8px;"><strong>Platform Administrator</strong></td>
                <td style="padding:4px 8px;">Everything, including users, workspaces, quotas and credentials.</td></tr>
          </table>
          <p style="margin-top:8px;">Every user manages their own second factor under
          <strong>Security</strong> in the account menu, whatever their role.</p>
          <p style="margin-top:8px;">If a screen you need is missing, ask a Platform Administrator to
          check your assigned role rather than looking for a different link.</p>
        `,
      },
      {
        id: 'security-mfa',
        label: 'Securing your account',
        title: 'Set up two-factor authentication',
        body: `
          <p><strong>Who this is for:</strong> everyone.</p>
          <p style="margin-top:8px;">Open <strong>Security</strong> from the account menu and choose
          <em>Set up authenticator</em>. Scan the QR code with Google Authenticator, Microsoft
          Authenticator, 1Password or any TOTP app, then type the six digits it shows to confirm.</p>
          <p style="margin-top:8px;">The code changes every 30 seconds and is generated on your own
          device — nothing is emailed or texted to you, so there is no code to intercept. The QR is
          drawn in your browser, so the secret is never sent to a third party.</p>
          <p style="margin-top:8px;">Lost your phone? A Platform Administrator can remove the
          factor so you can enrol again.</p>
        `,
      },
    ],
  },
  {
    id: 'guide',
    label: 'How To',
    icon: 'fa fa-route',
    topics: [
      {
        id: 'review-an-asset',
        label: 'Review & Edit an Asset',
        title: 'Reviewing, Commenting On and Editing an Asset',
        body: `
          <p>Click any image or clip — in the <strong>Media Library</strong>, on the home page, or in
          the studio's results — to open it full screen. The panel on the right has three tabs.</p>
          <p style="margin-top:12px;"><strong>Info</strong> — the full prompt with a copy button, the
          model that made it, size, seed, when it was created, and whether it carries a signed
          provenance manifest. <strong>Recreate</strong> and <strong>Use as reference</strong> both
          carry the prompt into the studio so you never retype it.</p>
          <p style="margin-top:12px;"><strong>Edit</strong> — <strong>Upscale 4&times;</strong> and
          <strong>Remove background</strong> run on the GPU. <strong>Reframe</strong> crops to 9:16,
          1:1, 4:5 or 16:9 for social, and <strong>Apply grade</strong> applies a colour look. Every
          edit saves a NEW asset linked to the original — your master is never altered.</p>
          <p style="margin-top:12px;"><strong>Comments</strong> — leave a note for the team. Comments
          are conversation and are kept separate from formal review decisions, which are recorded in
          <strong>Review &amp; Quality</strong>.</p>
          <p style="margin-top:8px;">The highlighted <strong>Turn to video</strong> button at the top
          animates a still into a clip. It appears only on stills, and only when a video model is
          registered.</p>
        `,
      },
      {
        id: 'set-up-projects',
        label: 'Set Up a Project',
        title: 'Setting Up a Project',
        body: `
          <p><strong>Who this is for:</strong> everyone. <strong>Projects</strong> is the home page:
          it lists your workspace's development projects, and opening one shows its Models, Units and
          Finishes tabs. Model administrators and platform administrators also create and edit projects here.</p>
          <p style="margin-top:12px;">To set one up, choose <strong>New project</strong>.
          Give it a name; leave the code blank and one is suggested from the name's initials. Add the
          city and the site latitude, longitude and true north, which are used when the model has no
          georeference.</p>
          <p style="margin-top:12px;">Under <strong>Advanced: unit grouping</strong>, a room number pattern
          with one capture group turns room numbers into unit keys. Type a room number to see the unit
          key it gives, or No match. Changes apply to the next ingest; existing revisions are not changed.</p>
        `,
      },
      {
        id: 'building-models',
        label: 'Upload a Building Model',
        title: 'Uploading a Building Model',
        body: `
          <p><strong>Who this is for:</strong> creative users and model administrators who bring a
          building into a project. Everyone else in the workspace can open the same tabs to read them.</p>
          <p style="margin-top:12px;">Open <strong>Projects</strong>, open the project and use its
          <strong>Models</strong> tab. Choose an IFC file exported from Revit with the PropStudioOS
          preset. Large files upload in pieces and pick up where they left off if your connection
          drops. You can switch tabs while a file uploads.</p>
          <p style="margin-top:12px;">After the upload the model is parsed in the background. The
          status moves from Queued to Parsing to Ready. If it fails, the reason is shown in the row.
          Uploading the exact same file again opens the existing revision instead of creating a
          duplicate. The <strong>Units</strong> tab shows the units of the latest ready model.</p>
        `,
      },
      {
        id: 'finish-schedules',
        label: 'Import a Finish Schedule',
        title: 'Importing a Finish Schedule',
        body: `
          <p><strong>Who this is for:</strong> creative users and model administrators who turn the interior designer's finish
          schedule into finish packages. Everyone else in the workspace can open the same tab to read them.</p>
          <p style="margin-top:12px;">Open <strong>Projects</strong>, open the project and use its <strong>Finishes</strong> tab.
          Choose <strong>Import a schedule</strong>.
          Download the Excel or CSV template, or upload the designer's own .xlsx or .csv file. Match each field to a column; space,
          surface and product code are required.</p>
          <p style="margin-top:12px;"><strong>Check the schedule</strong> validates every line against the latest parsed building model:
          unknown rooms, space types or unit types, unknown surfaces, missing codes and conflicting lines are reported by line number.
          <strong>Import schedule</strong> saves it as the next schedule version and creates a draft of each finish package it names.</p>
          <p style="margin-top:12px;">A room written once over its lines (a blank or merged cell) is repeated on each line below it,
          with a note, and so is a unit type cell merged down over several lines. A room number, or a room's own name such as
          "Bedroom 2", sets that room in every unit of its type, over the unit-type and all-unit-types lines. Write room names as the
          model has them: a numbered name the model does not have ("Bedroom 3") is refused, and other wording it does not have ("Ensuite")
          applies to every room of that type, with a warning. Packages list bindings by
          unit type, then room; a room finish that no longer matches a room in the latest model is flagged for re-approval.</p>
          <p style="margin-top:12px;">Switch the tab to <strong>Packages</strong>, set any missing material classes, correct product
          details or remove bindings, then <strong>Confirm package</strong>. Confirmed packages do not change; import a new schedule for
          the next version.</p>
        `,
      },
      {
        id: 'first-renders',
        label: 'Render Stills and Panoramas',
        title: 'Rendering Stills and 360° Panoramas',
        body: `
          <p><strong>Who this is for:</strong> creative users and model administrators who render rooms of a building model with
          its finish package. Everyone else in the workspace can open the same tab to see the renders.</p>
          <p style="margin-top:12px;">Open <strong>Projects</strong>, open the project and use its <strong>Renders</strong> tab.
          Choose a confirmed finish package, the rooms, a still, a 360° panorama or both, the quality and the sun's date and solar
          time, then <strong>Render</strong>. Draft is a quick check; Final is 4K for stills and 8K for panoramas, and slow.</p>
          <p style="margin-top:12px;">The job list shows progress, render time, the device used and peak memory. Finished renders open
          in the gallery: a panorama opens facing north (or the model's +Y when true north is not set); drag to look around. Surfaces with no finish in the package render in neutral
          clay and are listed under the image. A finish that needs re-approval on the Finishes tab is never shown: its surfaces render
          in grey with an amber grid, and the form warns before you start. Every render is an artist's impression and names its model
          and finish package.</p>
        `,
      },
      {
        id: 'import-media',
        label: 'Import Media',
        title: 'Bringing In Your Own Media and Stock',
        body: `
          <p>Not everything is generated. Press <strong>Import media</strong> in the
          <strong>Media Library</strong> to bring in footage you already have, or licensed stock.</p>
          <p style="margin-top:12px;"><strong>From this computer</strong> — drag a file onto the panel
          or click to choose one. Photos, video and audio are all accepted. Uploads are stored in your
          workspace and are never sent to an external service.</p>
          <p style="margin-top:12px;"><strong>Stock libraries</strong> — search connected providers and
          import a result straight into the library, where it behaves like any other asset. If the panel
          says no provider is configured, an administrator needs to add a provider key in the credential
          vault; results appear immediately afterwards with no redeploy.</p>
          <p style="margin-top:8px;">Imported items are tagged <strong>Shoot</strong> or
          <strong>Stock</strong> so you can always tell them apart from AI-generated work — useful when
          a client asks what in a package was generated and what was filmed.</p>
        `,
      },
      {
        id: 'register-and-monitor-models',
        label: 'Register & Monitor Model Endpoints',
        title: 'Registering & Monitoring Model Endpoints',
        // Documents the Model Registry / Registration Wizard / Worker & Runtime
        // Health flow (UC 2896e825) — Model Administrator + Platform Administrator.
        body: `
          <p>This walkthrough covers the full path for bringing a new model endpoint online: from
          the <strong>Model Registry</strong>, through the <strong>Registration Wizard</strong>, to
          watching it run on the <strong>Worker &amp; Runtime Health</strong> dashboard. It's
          available to the <strong>Model Administrator</strong> and <strong>Platform
          Administrator</strong> roles.</p>
          <p style="margin-top:12px;"><strong>1. Start from the Model Registry:</strong></p>
          <ul>
            <li>The registry lists every configured endpoint — CLIENT-hosted, CLIENT-GPU, internal
              API, and third-party API — with its <strong>modality</strong>, <strong>hosting
              class</strong>, <strong>endpoint health</strong>, <strong>cost profile</strong>, and
              lifecycle <strong>status</strong> (Active, Deprecated, Retired).</li>
            <li>Use the hosting class, modality, connector type, and status <strong>filters</strong>,
              or search by model name, family, or endpoint URL, to find an existing model.</li>
            <li>Click <strong>View Detail</strong> on any row for the full model descriptor, or
              <strong>Test Endpoint</strong> to trigger an on-demand health check without leaving
              the list. <strong>Deprecate Model</strong> retires a model from active use.</li>
          </ul>
          <p style="margin-top:12px;"><strong>2. Register a new endpoint with the wizard:</strong></p>
          <ol>
            <li><strong>Register New Model</strong> on the registry opens the six-step
              wizard.</li>
            <li style="margin-top:8px;"><strong>Connector Selection</strong> — choose the connector
              type and provider family.</li>
            <li style="margin-top:8px;"><strong>Endpoint &amp; Auth Config</strong> — enter the
              endpoint URL and network segment, then pick a <strong>secret reference</strong> for
              credentials. You always select a reference to a vaulted secret here — the wizard never
              accepts a literal API key.</li>
            <li style="margin-top:8px;"><strong>Descriptor Introspection</strong> — auto-query the
              endpoint's schema or upload a capability JSON descriptor.</li>
            <li style="margin-top:8px;"><strong>Capability &amp; Parameter Validation</strong> —
              review the imported parameters, defaults, and ranges before continuing.</li>
            <li style="margin-top:8px;"><strong>Test Execution</strong> — run a sample generation
              request against the endpoint and confirm the output looks right.</li>
            <li style="margin-top:8px;"><strong>Activation &amp; Licensing</strong> — declare the
              licence ID and commercial-use flag, then set the model <strong>Active</strong> to
              finish registration with zero code deployment.</li>
          </ol>
          <p style="margin-top:12px;"><strong>3. Watch it run on Worker &amp; Runtime Health:</strong></p>
          <ul>
            <li>The dashboard (Platform Administrator) lists every GPU worker node and runtime
              container: host capacity, active in-flight jobs, resident model weights, and
              <strong>GPU VRAM utilization</strong>.</li>
            <li>Each row's <strong>Health State</strong> badge shows Healthy, Degraded, Unreachable,
              or Draining. Use <strong>Ping Endpoint</strong> to verify connectivity,
              <strong>Restart Runtime</strong> to reinitialize a container, or <strong>Drain
              Worker</strong> to finish in-flight jobs while accepting no new ones.</li>
            <li>The <strong>GPU Utilization Over Time</strong> and <strong>Queue Depth vs. Wait
              Time</strong> charts help you spot capacity problems before they affect users.</li>
          </ul>
          <p style="margin-top:8px; font-size:13px; color:#a4a29f;">Tip: from the Registration
          Wizard, use <strong>Inspect Worker Health</strong> to jump straight to the runtime
          dashboard for the workers serving the model you just registered.</p>
        `,
      },
      {
        id: 'define-and-preview-capability-schemas',
        label: 'Define & Preview Capability Schemas',
        title: 'Defining & Previewing Model Capability Schemas',
        // Documents the Capability Descriptor Editor / Dynamic Parameter Panel
        // flow (UC e2769d36) — Model Administrator + Platform Administrator.
        body: `
          <p>The <strong>Capability Descriptor Editor</strong> and <strong>Dynamic Parameter
          Panel</strong> work together so a <strong>Model Administrator</strong> (or
          <strong>Platform Administrator</strong>) can define a model's capability schema and then
          immediately preview it as live controls — with no front-end code redeployment.</p>
          <p style="margin-top:12px;"><strong>1. Define the schema:</strong></p>
          <ul>
            <li>Open <strong>Capability Descriptor Editor</strong> from the Model Registry's
              <strong>Edit Capability Schema</strong> action, then pick the <strong>Model
              Version</strong> you're editing.</li>
            <li>Use the <strong>Parameter Definition Grid</strong> to add or edit each control: its
              key, type (Range, Enum, Boolean, Asset Ref), label, help tooltip, default value,
              min/max/step, group, display order, and visibility constraint.</li>
            <li>Use the <strong>Canonical Mapping Matrix</strong> to map canonical presets (e.g.
              "Slow Push-In") onto the model's native payload parameter keys and values.</li>
            <li>The <strong>Descriptor JSON Spec</strong> panel mirrors the grid as raw JSON — edit
              it directly to paste in a schema, or review it before publishing.</li>
          </ul>
          <p style="margin-top:12px;"><strong>2. Save, validate, and publish:</strong></p>
          <ul>
            <li><strong>Save Descriptor Version</strong> stores your in-progress edits as a
              draft.</li>
            <li><strong>Validate &amp; Publish Schema</strong> checks the JSON for errors — each
              finding lists its line number and jumps you straight to it — and blocks publishing
              until every error is resolved.</li>
            <li><strong>Export Schema JSON</strong> downloads the descriptor file.</li>
          </ul>
          <p style="margin-top:12px;"><strong>3. Preview it live:</strong></p>
          <p>Click <strong>Preview Dynamic Controls</strong> to open the <strong>Dynamic Parameter
          Panel</strong> for the model you were editing. Every control — sliders, dropdowns,
          toggles, asset references — renders straight from the published schema, grouped exactly
          as you defined it. Try a <strong>Quick Preset</strong> chip, adjust values, then
          <strong>Save As Preset</strong> to keep a custom combination, or <strong>Reset
          Defaults</strong> to start over. <strong>Generate Assets</strong> carries your current
          values into results generation.</p>
          <p style="margin-top:8px; font-size:13px; color:#a4a29f;">Tip: switch models from either
          page's model selector — the panel re-renders its controls the moment a different model's
          published schema loads.</p>
        `,
      },
      {
        id: 'generate-and-review-creative-assets',
        label: 'Generate & Review Creative Assets',
        title: 'Composing a Batch & Reviewing Its Variants',
        // Documents the Generation Studio + Results & Variants flow
        // (UC f94f75a9) — Creative User + Platform Administrator.
        // Updated by the "Workflow Templates" selector (UC 1287c5ff-…, AC #4 /
        // concern 00790426-93c5-4afd-9cff-d098e35a68fe).
        body: `
          <p>The <strong>Generation Studio</strong> and <strong>Results &amp; Variants</strong>
          screens together take a batch from prompt to finished picks. Both are available to the
          <strong>Creative User</strong> and <strong>Platform Administrator</strong> roles.</p>
          <p style="margin-top:12px;"><strong>1. Compose the batch in Generation Studio:</strong></p>
          <ul>
            <li>Pick a <strong>model</strong> and, optionally, a <strong>style preset</strong> — the
              preset can pre-fill prompt text and parameter values.</li>
            <li>If a Workflow Developer has published one or more workflow graphs as Creative Apps,
              a <strong>Workflow Templates</strong> dropdown appears beneath the style preset
              selector. Choosing one prefills the positive prompt with that workflow's prompt prefix
              and applies its numeric parameter defaults onto any dynamic parameter the active model
              also publishes — the same mechanic as a style preset, just sourced from a published
              workflow instead. It only appears once at least one workflow-derived template has been
              published; with none published yet, the dropdown is simply absent.</li>
            <li>Write your <strong>positive prompt</strong> (what you want) and an optional
              <strong>negative prompt</strong> (what to avoid). The running <strong>token
              count</strong> blocks submission once you go over the model's limit, so trim before
              you generate.</li>
            <li>Drop or attach a <strong>reference image</strong> if the model supports image
              conditioning — remove it with the clear action if you change your mind.</li>
            <li>Adjust the <strong>dynamic parameters</strong> (CFG / guidance scale, sampling
              steps, aspect ratio, and any others the model's published schema defines) with their
              sliders — the same controls the Dynamic Parameter Panel renders.</li>
            <li>Set the <strong>seed</strong> manually, or toggle <strong>Lock Seed</strong> to keep
              reusing the same seed across runs instead of randomizing it each time.</li>
          </ul>
          <p style="margin-top:12px;"><strong>2. Generate:</strong></p>
          <p>Click <strong>Generate Assets</strong> to submit the batch. A live progress bar tracks
          the run; once it completes, the page scrolls you down to the newly generated outputs so
          you can mark a <strong>winner</strong> right from this screen.</p>
          <p style="margin-top:12px;"><strong>Ask Xami before you generate:</strong></p>
          <ul>
            <li>Open the <strong>Xami</strong> panel and click <strong>Enhance Prompt</strong>
              to get real, generated descriptive additions, a lighting cue, a camera parameter, an
              atmosphere cue, and optimized negative-prompt keywords for your current positive
              prompt. Click <strong>Apply to Prompt</strong> to merge them straight into the
              positive/negative prompt boxes — the token count is checked against the active
              model's real context length before anything is applied.</li>
            <li>Type a goal into the Copilot chat — e.g. &ldquo;Recommend optimal sampler settings
              for photorealistic architectural rendering&rdquo; or &ldquo;convert to impressionist
              oil painting&rdquo; — to get a real CFG / Sampler / Steps recommendation (and, for a
              style conversion, style modifier tags) drawn from the active model's own published
              parameter schema. Click <strong>Apply Settings</strong> to move the actual sliders
              and dropdowns instantly, no reload.</li>
            <li>A request that trips an active brand guardrail (e.g. an extreme-saturation or
              high-contrast style) gets a compliance notice naming the violated rule plus a
              brand-compliant alternative, instead of settings to apply.</li>
          </ul>
          <p style="margin-top:8px; font-size:13px; color:#a4a29f;">Tip: if Copilot doesn't respond
          in time, it shows an error toast and reverts to your original prompt — nothing is applied
          on a failed request.</p>
          <p style="margin-top:12px;"><strong>3. Review variants in Results &amp; Variants:</strong></p>
          <ul>
            <li>Each generated variant shows in the grid with hover actions: <strong>stage
              it</strong> to the variant tray for comparison, mark it a <strong>favorite</strong>,
              <strong>download</strong> it, or <strong>Save to Project</strong> to file it straight
              into <strong>Asset Vault &amp; Projects</strong> without leaving this screen.</li>
            <li>Every card's metadata row carries its <strong>resolution</strong> badge alongside
              the <strong>CFG</strong> (guidance scale) and <strong>Steps</strong> badges, plus the
              generation time in milliseconds, so you can compare settings at a glance without
              opening each asset.</li>
            <li>Staged variants collect in the floating <strong>variant tray</strong> at the bottom
              of the screen — use <strong>Clear Tray</strong> to empty it.</li>
            <li>Once you've staged the picks you want, <strong>Submit to Review</strong> from the
              tray to send them forward.</li>
          </ul>
          <p style="margin-top:12px;"><strong>If a variant is flagged:</strong> a result that fails
          the platform's content-safety check renders differently — its thumbnail is blurred, and a
          <strong>Content Safety Flagged (Code 451)</strong> badge replaces the usual overlay
          actions, with a short panel explaining why it was withheld. Since a withheld asset has
          nothing to stage, favorite, download, or save, its card shows only <strong>Retry with
          modified prompt</strong>, which reopens <strong>Generation Studio</strong> with that
          batch's prompt and model already filled in so you can revise the prompt and resubmit.</p>
          <p style="margin-top:8px; font-size:13px; color:#a4a29f;">Tip: reach Generation Studio
          from the App Gallery's <strong>Launch Creative Studio</strong> card — Results &amp;
          Variants opens automatically once a batch you submitted finishes.</p>
        `,
      },
      {
        id: 'compare-variants-and-select-winner',
        label: 'Compare Variants & Select a Winner',
        title: 'Comparing Variants Side-by-Side & Selecting a Winner',
        // Documents the Comparison View Screen + the "Compare Selected" trigger
        // added to Results & Variants (UC f7976ca2) — Creative User.
        body: `
          <p>Once you've generated a batch, use <strong>Compare Variants</strong> to inspect two to
          four candidates side-by-side and pick the one that ships. This flow spans two screens:
          <strong>Results &amp; Variants</strong>, where you stage the candidates, and the
          <strong>Comparison View</strong>, where you actually compare them.</p>
          <p style="margin-top:12px;"><strong>1. Stage variants from Results &amp; Variants:</strong></p>
          <ul>
            <li>Multi-select the variants you want to weigh against each other directly in the
              results grid — check any combination of <strong>2 to 4</strong> variants.</li>
            <li>Click <strong>Compare Selected</strong> to open the Comparison View with exactly
              those variants loaded.</li>
            <li>If you try to select a fifth variant, a warning toast tells you the
              <strong>4-variant maximum</strong> has been reached — deselect one before adding
              another.</li>
          </ul>
          <p style="margin-top:12px;"><strong>2. Inspect them in Comparison View:</strong></p>
          <ul>
            <li>Each staged variant renders in its own pane with synchronized
              <strong>pan and zoom</strong> — drag or zoom in any pane and the others follow, so
              you're always looking at the same region across every candidate.</li>
            <li>The <strong>parameter diff table</strong> lists prompt, CFG/guidance scale, sampling
              steps, and seed side-by-side, highlighting exactly which values differ between the
              variants you staged. Toggle <strong>Only Diffs</strong> to hide rows that match across
              every variant and focus on what changed.</li>
            <li>Drag the <strong>pixel-level diff slider</strong> across two panes to reveal a
              before/after wipe — the fastest way to spot subtle visual differences a side-by-side
              glance can miss.</li>
          </ul>
          <p style="margin-top:12px;"><strong>3. Select the winner:</strong></p>
          <p>Click <strong>Select Winner</strong>, confirm your pick in the dialog, and that variant
          is tagged as the <strong>Primary Output</strong> for the batch — the one that carries
          forward to review and export.</p>
          <p style="margin-top:8px; font-size:13px; color:#a4a29f;">Tip: Select Winner stays
          disabled until the page finishes loading and at least two variants are ready to
          compare.</p>
        `,
      },
      {
        id: 'browse-vault-and-trace-lineage',
        label: 'Browse the Vault & Trace Lineage',
        title: 'Browsing Project Vaults & Tracing Asset Lineage',
        // Documents the Asset Vault & Projects + Asset Detail & Lineage View
        // flow (UC 6073ba2f) — Creative User + Platform Administrator.
        body: `
          <p><strong>Asset Vault &amp; Projects</strong> and <strong>Asset Detail &amp; Lineage
          View</strong> together let you organize campaign work into projects and trace exactly how
          any generated asset came to be. Both are available to the <strong>Creative User</strong>
          and <strong>Platform Administrator</strong> roles.</p>
          <p style="margin-top:12px;"><strong>1. Browse projects in Asset Vault &amp; Projects:</strong></p>
          <ul>
            <li>The four summary cards at the top — <strong>Active Projects</strong>, <strong>Vaulted
              Assets</strong>, <strong>Pending Approvals</strong>, and <strong>Storage Usage</strong>
              — give you a workspace-wide snapshot at a glance.</li>
            <li>Use the <strong>search box</strong> to find a project by name, client, or tag, the
              <strong>category filter</strong> to narrow by campaign type, and the
              <strong>Grid/Table</strong> toggle to switch how projects are laid out.</li>
            <li>Click any project card to open its detail view with three tabs: <strong>Campaign
              Assets</strong> (the project's asset list and uploads), <strong>Team Access &amp;
              Permissions</strong> (who has access and at what level), and <strong>Audit Activity
              Stream</strong> (a running log of everything that happened on the project).</li>
            <li>Use <strong>Export Campaign Package</strong> to download a project's finalized files,
              or <strong>Create New Project</strong> to start a new campaign workspace.</li>
          </ul>
          <p style="margin-top:12px;"><strong>2. Open an asset's lineage:</strong></p>
          <p>From a project's asset list, click <strong>Inspect Asset Lineage</strong> on any asset to
          open <strong>Asset Detail &amp; Lineage View</strong> for that specific file.</p>
          <p style="margin-top:12px;"><strong>3. Inspect and trace it in Asset Detail &amp; Lineage View:</strong></p>
          <ul>
            <li>The <strong>Generation Parameters</strong> card shows the exact positive/negative
              prompt, <strong>CFG scale</strong>, <strong>steps</strong>, and <strong>sampler</strong>
              used to produce the asset — use <strong>Copy Prompt</strong> to reuse the positive
              prompt elsewhere.</li>
            <li>The <strong>C2PA Cryptographic Signature</strong> card confirms the asset's
              tamper-evident manifest is verified, with its signing entity, manifest digest, and
              timestamp.</li>
            <li>The <strong>lineage graph</strong> renders the asset's full derivation history as a
              node diagram. Click any ancestor node to open its <strong>Stored Parameters</strong>
              panel and see exactly which values changed between generations.</li>
            <li>From that panel, click <strong>Fork Generation</strong> to jump back into
              <strong>Generation Studio</strong> with that ancestor's model, seed, prompt, and
              parameters already filled in — the fastest way to branch a new variation from any point
              in an asset's history.</li>
            <li>The <strong>Asset Version History</strong> timeline lists every prior version of the
              asset in order, so you can see how it evolved over time.</li>
            <li>Use the <strong>Export Asset</strong> dropdown to download the file as the
              <strong>Original PNG</strong>, the <strong>C2PA Manifest JSON</strong>, or a
              <strong>Complete Zip Bundle</strong> containing both. If your workspace role is
              <strong>View-Only</strong>, exporting is blocked and you'll see an on-screen error —
              ask a workspace admin to raise your access level to export source media.</li>
            <li>The <strong>Organize Asset</strong> card lets you file the asset for the long
              term: type a path like <strong>Campaign_2025/Hero_Images</strong> into
              <strong>Project Folder</strong> and click <strong>Move</strong> to relocate it in the
              vault's folder hierarchy; add or remove <strong>tags</strong> as chips — type a
              word and press Enter (or click <strong>Add</strong>), then <strong>Save
              Tags</strong> — for fast filtering later; and set its <strong>Lifecycle
              Status</strong> from the dropdown as it moves through <strong>Draft</strong>,
              <strong>Under Review</strong>, <strong>Approved</strong>, and
              <strong>Archived</strong>.</li>
          </ul>
          <p style="margin-top:8px; font-size:13px; color:#a4a29f;">Tip: reach Asset Vault &amp;
          Projects from the <strong>Creative Studio</strong> navigation group — lineage is always
          opened from a specific asset, never as a standalone screen.</p>
        `,
      },
      {
        id: 'manage-brand-elements-and-presets',
        label: 'Brand Elements, Presets & Guardrails',
        title: 'Managing Brand Elements, Reusable Presets & Guardrails',
        // Documents the Brand & Creative Element Manager page (page a4789f24…)
        // plus its Generation Studio integration points (UC 942f42d3) —
        // Creative User + Platform Administrator.
        body: `
          <p><strong>Brand &amp; Creative Element Manager</strong> is where a <strong>Creative
          User</strong> builds a shared library of brand assets — logos, color palettes, dynamic
          parameter presets, and content guardrails — then applies any of them straight into
          <strong>Generation Studio</strong>. Reach it from the App Gallery's
          <strong>Open Brand &amp; Presets Manager</strong> card.</p>
          <p style="margin-top:12px;"><strong>1. Build your library:</strong></p>
          <ul>
            <li>Switch between the <strong>Logos &amp; Watermarks</strong>, <strong>Color
              Palettes</strong>, <strong>Dynamic Presets</strong>, and <strong>Guardrails</strong>
              tabs, or use the search box to filter by name across all of them.</li>
            <li><strong>Add Brand Element</strong> opens the create form. A watermark or palette
              upload shares one combined form — attach a logo file, add one or more entries with the
              <strong>hex-color picker</strong>, or both together — since both build the same Brand
              Style Pack. Choosing <strong>Style Guardrail Policy</strong> switches the form to a
              keyword-chip editor: type a prohibited term and press Enter (or click the
              <strong>+</strong> button) to add it as a removable chip.</li>
          </ul>
          <p style="margin-top:12px;"><strong>2. Inspect and apply an element:</strong></p>
          <p>Click any card to open its <strong>Detail Inspector</strong>. A color palette shows an
          <strong>Enforce Color Palette Guardrail</strong> toggle — switch it on to make that
          palette a hard rule checked on every generation, not just a suggestion. Every element type
          has an <strong>Apply to Generation Studio</strong> button: for a logo or palette it opens
          Generation Studio with that Brand Style Pack already attached; for a guardrail it activates
          the rule and takes you there directly.</p>
          <p style="margin-top:12px;"><strong>3. Using a style pack in Generation Studio:</strong></p>
          <p>The Prompt Composer's <strong>Brand Style Pack</strong> selector attaches a saved pack
          to the current batch — its hex swatches preview under the dropdown, and a
          <strong>Watermark Attached</strong> badge appears if the pack carries a logo. Once
          attached, the palette auto-injects into the prompt pipeline and the logo watermarks every
          resulting variant (look for the <strong>Watermark Applied</strong> badge on each result
          card). If your prompt contains a word an active guardrail prohibits, a red
          <strong>Brand Guardrail Violation</strong> badge appears, the prompt box outlines red, and
          <strong>Generate</strong> stays disabled until you remove the flagged word.</p>
          <p style="margin-top:12px;"><strong>4. Saving and reusing Dynamic Presets:</strong></p>
          <p>Click <strong>Reusable Presets</strong> in the Studio Header Bar to open the presets
          manager. <strong>Save as Preset</strong> stores your current sliders, model, prompt, and
          sampler under a name, a <strong>category</strong> (Photorealistic, Isometric 3D, Vector
          Art, Brand Marketing), and a <strong>Workspace Shared</strong> or <strong>Private</strong>
          visibility tag. Browse saved presets by category tab and click <strong>Apply Preset</strong>
          to overwrite the active canvas instantly. <strong>Save Overwrite Preset</strong> updates the
          preset you loaded — except a built-in <strong>Global System</strong> preset (e.g. "Default
          FLUX Studio Baseline"), which can never be overwritten.</p>
          <p style="margin-top:8px; font-size:13px; color:#a4a29f;">Tip: a preset you save here
          immediately shows up back in the Brand &amp; Creative Element Manager's Dynamic Presets
          tab — the two screens share the same preset library.</p>
        `,
      },
      {
        // Documents the brand control plane shipped 2026-08-21: the guardrail
        // rules that refuse or flag a prompt, and where brand is applied from.
        id: 'keep-work-on-brand',
        label: 'Keep work on-brand',
        title: 'Brand rules, and how brand reaches your renders',
        body: `
          <p><strong>Who this is for:</strong> creatives who hit a refused prompt and want to know
          why, and platform administrators who decide what the rules are.</p>
          <p style="margin-top:8px;">Everything under <strong>Brand &amp; Identity</strong> governs
          what comes out of the generators. It has three parts:</p>
          <ul>
            <li><strong>Brand Kit</strong> — your palette, logo and presets. When a pack is active
              its colours are added to every prompt and its logo is composited onto the output. The
              Generation Studio shows a chip above the prompt naming the pack and its palette, so
              you can always see what is being applied before you generate.</li>
            <li><strong>Looks &amp; People</strong> — trained looks, materials and products, so
              later work stays visually consistent. Also reached from the <strong>Looks &amp;
              People</strong> tile on the App Gallery.</li>
            <li><strong>Brand Rules</strong> — the words your workspace will not publish.</li>
          </ul>
          <p style="margin-top:12px;"><strong>What a rule does when it fires.</strong> Every rule has
          a severity:</p>
          <ul>
            <li><strong>Blocks submission</strong> — the generation is refused, and a dialog opens
              naming the rule and the exact word or phrase that matched, with the rule's suggested
              alternative when it has one. If the work genuinely needs to go ahead, type a reason of
              at least ten characters and confirm: the job then runs, and that reason is recorded
              against it and visible in <strong>Compliance &amp; Audit</strong> — it is not a private
              note. Cancelling the dialog abandons the submission rather than sending it anyway.</li>
            <li><strong>Flags for review</strong> — the generation runs normally, and the finished
              asset is sent to the <strong>Review Queue</strong> for an approver to look at before
              it ships.</li>
          </ul>
          <p style="margin-top:12px;">Rules apply everywhere work is submitted — the Generation
          Studio, turning a still into a clip, and the public workflow API. A rule cannot be dodged
          by moving the word into the negative prompt, and a prompt written in Arabic is re-checked
          after it is translated.</p>
          <p style="margin-top:12px;"><strong>Managing rules.</strong> Any signed-in user can read
          the rules on the <strong>Brand Rules</strong> page — if a rule refused your work, you can
          see exactly which one. Only a Platform Administrator can add a rule or switch one off.
          A workspace with no rules blocks nothing.</p>
        `,
      },
      {
        id: 'build-test-publish-workflows',
        label: 'Building & Publishing Workflows',
        title: 'Building, Testing & Publishing Visual Workflows',
        // Bootstrapped by the Workflow Engine & Runtime Adapter activity (UC 1287c5ff).
        body: `
          <p>The <strong>Workflow Editor</strong> is where a Workflow Developer builds a visual,
          node-based automation graph — drag nodes from the palette onto the canvas, wire them
          together, then test and publish the result as a reusable Creative App.</p>
          <p style="margin-top:12px;"><strong>Building the graph:</strong></p>
          <ol>
            <li><strong>Create New Workflow Graph</strong> — name it, pick a category and modality,
              then start building on the canvas that appears below the catalog table.</li>
            <li><strong>Drag a node</strong> from the palette (Prompt Transformer, Model Endpoint,
              Style Filter, Image Generator, Image Inpainting Mask, Output Export) onto the canvas.</li>
            <li style="margin-top:8px;"><strong>Connect ports</strong> — drag from an output port
              (right edge of a node) to an input port (left edge of another). The canvas only allows
              a connection when the two ports' data types match; an incompatible attempt (e.g. an
              Image Output into an Integer Seed input) is rejected on the spot, with the mismatched
              port highlighted red and a tooltip explaining why.</li>
            <li style="margin-top:8px;"><strong>Save DAG Pipeline</strong> saves your draft; the
              server re-checks every connection independently before it is stored.</li>
          </ol>
          <p style="margin-top:12px;"><strong>Testing a draft:</strong></p>
          <ul>
            <li>From the editor, click <strong>Test Workflow</strong> to open the
              <strong>Workflow Test Harness</strong> for that graph.</li>
            <li>Fill in the dynamic input form — one field per port your graph leaves unconnected —
              then click <strong>Execute Dry-Run Test</strong>.</li>
            <li>Each node's simulated output and execution time appear in its own tab, alongside a
              live log console you can filter by level.</li>
          </ul>
          <p style="margin-top:12px;"><strong>Publishing as a Creative App:</strong></p>
          <ul>
            <li>From the Test Harness, click <strong>Configure App Binding</strong> to open the
              <strong>Creative App Builder &amp; Binding Editor</strong>.</li>
            <li>Give the app a name, description, category and icon, choose which platform roles can
              use it, and map each workflow input to a simplified end-user control in the
              <strong>Parameter Binding Matrix</strong>.</li>
            <li>Click <strong>Publish Creative App</strong> — it immediately appears as a new card in
              the <strong>App Gallery</strong>, ready for other workspace users to launch.</li>
          </ul>
          <p style="margin-top:8px; font-size:13px; color:#a4a29f;">Tip: back on the Workflow Editor,
          use <strong>Promote to Production</strong> once a draft graph is ready to be the workflow's
          active, published version.</p>
        `,
      },
      {
        // Documents turn-to-video and sized downloads (shipped 2026-08-20).
        id: 'turn-a-still-into-a-clip',
        label: 'Turn a still into a clip',
        title: 'Animating a still, and downloading at the right size',
        body: `
          <p><strong>Who this is for:</strong> anyone who has an image — generated, imported from a
          stock library, or uploaded from a shoot — and wants a short clip of it.</p>
          <p style="margin-top:8px;">Open any still from the <strong>Media Library</strong> and use
          <strong>Turn to video</strong>. Three choices sit under the button, each showing what it
          costs in time:</p>
          <ul>
            <li><strong>Model</strong> — a hosted API returns an 8-second clip with audio in about
              two minutes; your own GPU returns a 5-second silent clip and takes considerably
              longer. The wait is printed on the button before you click.</li>
            <li><strong>Shape</strong> — landscape, portrait, or the still's own shape. A square
              still sent to a hosted model is letterboxed, because those models render 16:9 or 9:16
              only.</li>
            <li><strong>Quality</strong> — only on your own GPU, where sampling steps are yours to
              trade: Draft is fastest, High is slowest and most detailed.</li>
          </ul>
          <p style="margin-top:12px;">The render continues if you close the panel, and the clip
          appears in your library when it lands. Imported stock and uploaded footage can be animated
          exactly like generated images.</p>
          <p style="margin-top:12px;"><strong>Brand and likeness carry forward.</strong> Animating a
          still carries that still's brand pack and its Look into the clip, and says so before you
          click. If the Look is no longer approved, its consent has lapsed, or the model you picked
          cannot apply it, that is stated on the panel and the Look is dropped from the clip rather
          than used anyway. You can also decline the carry-over yourself if you would rather the clip
          come out unbranded and unpinned.</p>
          <p style="margin-top:8px;">One change worth knowing about: a still that was generated with
          no brand pack now stays unbranded when you animate it. It used to pick up your workspace's
          default ident instead — if that is not what you expect, this is why.</p>
          <p style="margin-top:12px;"><strong>Downloading.</strong> Every asset offers its original
          plus any smaller size it can honestly produce — named by the short edge, the way delivery
          specs are written. A 720p master is never offered at 1080p, because enlarging it would
          invent detail and return a bigger file that looks softer. The original is always the
          untouched file.</p>
        `,
      },
      {
        id: 'command-center',
        label: 'Command Center',
        title: 'Watch what the platform is actually doing',
        body: `
          <p><strong>Who this is for:</strong> Model and Platform Administrators.</p>
          <p style="margin-top:8px;">The <strong>Command Center</strong> is the operations view:
          the live job queue, the GPU worker fleet, routing decisions and the event feed.</p>
          <ul style="margin-top:8px;padding-left:18px;">
            <li><strong>LIVE vs SIMULATED.</strong> Workers measured from real hardware are badged
              LIVE; modelled ones are badged SIMULATED. The two are never styled the same.</li>
            <li><strong>GPU state</strong> shows asleep, waking or ready. A stopped GPU costs
              nothing but takes about 90 seconds to come back.</li>
            <li><strong>Queue</strong> shows each job's real progress, and lets you cancel one that
              is no longer wanted.</li>
          </ul>
          <p style="margin-top:8px;">A dash rather than a number means that value was not measured.
          It is deliberately not shown as zero.</p>
        `,
      },
      {
        id: 'media-library',
        label: 'Media Library',
        title: 'Everything the studio can draw on',
        body: `
          <p><strong>Who this is for:</strong> everyone who makes or reviews work.</p>
          <p style="margin-top:8px;">The <strong>Media Library</strong> holds AI generations, real
          shoot footage and licensed stock, separated by modality — Images, Video, Audio and Motion
          Graphics — and filterable by origin.</p>
          <p style="margin-top:8px;">Anything here can be attached in the Generation Studio as a reference, so a real photograph and a generated
          frame can sit in the same sequence.</p>
          <p style="margin-top:8px;">Use <strong>Upload shoot media</strong> to bring in footage
          from a real shoot.</p>
        `,
      },
      {
        id: 'use-creative-assistant-panel',
        label: 'Using the Creative Assistant Panel',
        title: 'Turning a Plain-Language Brief into a Generation Request',
        // Documents the Creative Assistant Panel screen (page dc49a37b…),
        // UC 47086259 ("Copilot") — Creative User, Senior Creative / Approver,
        // Platform Administrator. Resolves concern 9ac38edb (missing has_page
        // linkage) — the page itself was already reachable via the "Creative
        // Assistant" link in Generation Studio's header bar.
        body: `
          <p>The <strong>Creative Assistant Panel</strong> lets you describe what you want in plain
          language and have Xami turn it into a ready-to-run generation request — no need
          to hand-fill prompts, camera settings, or model choice yourself. Open it from the
          <strong>Creative Assistant</strong> link in <strong>Generation Studio's</strong> header bar.
          It's available to the <strong>Creative User</strong>, <strong>Senior Creative /
          Approver</strong>, and <strong>Platform Administrator</strong> roles.</p>
          <p style="margin-top:12px;"><strong>1. Describe your brief:</strong></p>
          <p>Type what you're picturing into the chat box — for example, "Create a dramatic
          night-time shot of the living room with the camera approaching the balcony
          overlooking the marina." Plain, conversational language works fine; both English and
          Arabic are supported.</p>
          <p style="margin-top:12px;"><strong>2. Interpret it:</strong></p>
          <p>Click <strong>Interpret</strong> (or press Enter) to send the brief to Xami.
          It replies in the chat and fills the <strong>Structured Request Preview</strong> panel on
          the right with an enhanced positive prompt, a recommended negative prompt, a suggested
          <strong>camera motion</strong>, the <strong>target modality</strong> (image or video), and a
          <strong>recommended model</strong>. If a brand preset is attached to your workspace, its
          name shows in the <strong>Brand Preset Attached</strong> badge.</p>
          <p style="margin-top:12px;"><strong>3. Adjust or refine:</strong></p>
          <p>Edit the prompt text, camera motion, or target modality directly in the preview panel if
          the AI's first pass isn't quite right, or click <strong>Refine Prompt</strong> to jump back
          to the chat box and add more detail before interpreting again.</p>
          <p style="margin-top:12px;"><strong>4. Confirm and execute:</strong></p>
          <p>Once the preview looks right, click <strong>Confirm &amp; Execute Request</strong> to
          dispatch a real generation job with those settings. You're taken straight to
          <strong>Results &amp; Variants</strong> to watch it run and review the outputs.</p>
          <p style="margin-top:8px; font-size:13px; color:#a4a29f;">Tip: every request passes an
          egress and safety check (the <strong>Egress &amp; Safety Checked</strong> badge) before it
          reaches the preview — if your brief trips a brand guardrail, the Copilot explains why and
          suggests a compliant alternative instead of a request to confirm.</p>
        `,
      },
    ],
  },
  {
    id: 'faq',
    label: 'FAQ',
    icon: 'fa fa-question-circle',
    topics: [
      {
        id: 'audit-search-export',
        label: 'Audit Search & Export',
        title: 'Searching, Verifying & Exporting the Audit Log',
        // Documents the Audit Search & Export screen (page af9ea1b9…),
        // UC be4a90a6 — Auditor / Compliance + Platform Administrator.
        body: `
          <p>The <strong>Audit Search &amp; Export</strong> screen — reachable as
          <strong>Compliance &amp; Audit</strong> in the navigation, and gated to the
          <strong>Auditor / Compliance</strong> and <strong>Platform Administrator</strong> roles —
          is your window into the platform's immutable, append-only activity log.</p>
          <p style="margin-top:12px;"><strong>Searching and filtering:</strong></p>
          <ul>
            <li>Use the <strong>search box</strong> to find entries by Event ID, target entity, or
              user email.</li>
            <li>Narrow results with the <strong>Action Type</strong> filter — including the
              <strong>Asset Approval</strong> option alongside credential and access-control
              actions — the <strong>Actor Type</strong> filter (Human User vs. Service Account /
              API), and the <strong>Date Range</strong> picker.</li>
            <li>Click the <strong>Reset</strong> button to clear every filter back to the full
              directory.</li>
          </ul>
          <p style="margin-top:12px;"><strong>Verifying integrity:</strong></p>
          <p>Click <strong>Verify Hash Integrity</strong> to walk the log's SHA-256 hash chain end
          to end — the <strong>Hash Chain Status</strong> card updates to <strong>Verified
          Valid</strong>, or flags a <strong>Tamper Warning</strong> naming the exact event where
          the chain breaks. Click <strong>Payload</strong> on any row to open that entry's full
          audit payload, including its C2PA-linked provenance data, and <strong>Verify</strong> its
          hash independently.</p>
          <p style="margin-top:12px;"><strong>Exporting for compliance:</strong></p>
          <p>Use <strong>Export Signed Log (JSON)</strong> or <strong>Export Encrypted Audit Report
          (CSV)</strong> to download a cryptographically signed copy of the currently filtered log
          for regulators or internal compliance review.</p>
          <p style="margin-top:8px; font-size:13px; color:#a4a29f;">Tip: the four summary cards at
          the top of the screen — Total Logged Events, Hash Chain Status, Security &amp; RBAC
          Audits, and Latest SHA-256 Block — give you a workspace-wide compliance snapshot at a
          glance.</p>
        `,
      },
      {
        id: 'review-approval-queue',
        label: 'Reviewing & Approving Assets',
        title: 'Reviewing, Annotating & Approving Creative Assets',
        // Documents the Review & Approval Queue screen (page cddcde15…),
        // UC 3154b64a — Senior Creative / Approver + Platform Administrator.
        body: `
          <p>The <strong>Review &amp; Approval Queue</strong> — reachable from the App Gallery's
          pending-approvals widget or the <strong>Review &amp; Quality</strong> navigation group,
          and gated to the <strong>Senior Creative / Approver</strong> and <strong>Platform
          Administrator</strong> roles — is where submitted creative assets get inspected and
          decided on.</p>
          <p style="margin-top:12px;"><strong>Finding an asset:</strong></p>
          <ul>
            <li>Use the <strong>search box</strong> to find a submission by prompt, creator, or
              asset ID, or narrow the table with the <strong>Status</strong>, <strong>Workspace</strong>,
              and <strong>Creator</strong> filters. Click <strong>Reset</strong> to clear all of
              them at once.</li>
            <li>If your filters match nothing, the table shows an empty-state panel — click
              <strong>Clear All Filters</strong> to return to the full queue.</li>
            <li>Click <strong>Review Asset</strong> (or <strong>View Details</strong> for an
              already-decided submission) on any row to open the review modal.</li>
          </ul>
          <p style="margin-top:12px;"><strong>Inspecting the asset:</strong></p>
          <p>The <strong>Generation Lineage &amp; Metadata</strong> card shows the model, CFG/steps,
          seed, brand-compliance percentage, content-safety result, and C2PA provenance status. Any
          prior review's feedback appears above it in the <strong>feedback history</strong>.</p>
          <p style="margin-top:12px;"><strong>Annotating:</strong></p>
          <p>On the asset image, use <strong>Add Pin</strong> to drop a point marker, or
          <strong>Add Box</strong> to draw a real bounding box — click-drag to size it, then drag or
          resize it afterward. <strong>Clear</strong> removes every annotation. Annotations are
          saved with your decision and notify the creator.</p>
          <p style="margin-top:12px;"><strong>Deciding:</strong></p>
          <p>Enter your notes in the <strong>feedback textarea</strong> and, if relevant, pick a
          <strong>rejection reason</strong>. <strong>Reject Asset</strong> and <strong>Request
          Changes</strong> both require a non-empty feedback note before they'll submit;
          <strong>Approve &amp; Publish</strong> does not, and immediately files the asset into the
          project vault while logging an audit event.</p>
          <p style="margin-top:8px; font-size:13px; color:#a4a29f;">Tip: an asset already Approved
          or Rejected opens read-only — its decision buttons are replaced with a status
          notice.</p>
        `,
      },
    ],
  },
];

/** Default section/topic the drawer opens to when no route match applies. */
export const DEFAULT_TOPIC = { sectionId: 'welcome', topicId: 'getting-started' };

/**
 * Route → { sectionId, topicId } lookup, so the drawer can open pre-scrolled to
 * the topic covering the page the user is currently on. Extend this map as new
 * pages/topics are added — never hard-code the mapping inline in a component.
 */
const ROUTE_TOPIC_MAP = {
  '/': { sectionId: 'guide', topicId: 'set-up-projects' },
  '/app-gallery': { sectionId: 'identity-access-workspaces', topicId: 'app-gallery' },
  '/users-roles': { sectionId: 'identity-access-workspaces', topicId: 'users-groups-roles' },
  '/sign-in': { sectionId: 'identity-access-workspaces', topicId: 'sign-in-sso' },
  '/projects': { sectionId: 'guide', topicId: 'set-up-projects' },
  '/model-ingest': { sectionId: 'guide', topicId: 'building-models' },
  '/model-registry': { sectionId: 'guide', topicId: 'register-and-monitor-models' },
  '/model-registration-wizard': { sectionId: 'guide', topicId: 'register-and-monitor-models' },
  '/worker-runtime-health': { sectionId: 'guide', topicId: 'register-and-monitor-models' },
  '/capability-descriptor-editor': {
    sectionId: 'guide',
    topicId: 'define-and-preview-capability-schemas',
  },
  '/dynamic-parameter-panel': {
    sectionId: 'guide',
    topicId: 'define-and-preview-capability-schemas',
  },
  '/generation-studio': {
    sectionId: 'guide',
    topicId: 'generate-and-review-creative-assets',
  },
  '/brand-elements-presets': {
    sectionId: 'guide',
    topicId: 'manage-brand-elements-and-presets',
  },
  '/results-variants': {
    sectionId: 'guide',
    topicId: 'generate-and-review-creative-assets',
  },
  '/comparison-view': {
    sectionId: 'guide',
    topicId: 'compare-variants-and-select-winner',
  },
  '/asset-vault-projects': {
    sectionId: 'guide',
    topicId: 'browse-vault-and-trace-lineage',
  },
  '/asset-detail-lineage': {
    sectionId: 'guide',
    topicId: 'browse-vault-and-trace-lineage',
  },
  '/creative-assistant-panel': {
    sectionId: 'guide',
    topicId: 'use-creative-assistant-panel',
  },
  '/workflow-editor': {
    sectionId: 'guide',
    topicId: 'build-test-publish-workflows',
  },
  '/workflow-test-harness': {
    sectionId: 'guide',
    topicId: 'build-test-publish-workflows',
  },
  '/creative-app-builder-binding-editor': {
    sectionId: 'guide',
    topicId: 'build-test-publish-workflows',
  },
  '/compliance-audit': {
    sectionId: 'faq',
    topicId: 'audit-search-export',
  },
  '/review-queue': {
    sectionId: 'faq',
    topicId: 'review-approval-queue',
  },
  '/orchestration-command-center': {
    sectionId: 'guide',
    topicId: 'command-center',
  },
  '/media-library': {
    sectionId: 'guide',
    topicId: 'media-library',
  },
  // The Brand Kit page keeps its own topic further down; only the new Rules
  // page needs mapping here.
  '/brand-rules': {
    sectionId: 'guide',
    topicId: 'keep-work-on-brand',
  },
  '/security': {
    sectionId: 'roles',
    topicId: 'security-mfa',
  },
};

/**
 * Resolve the { sectionId, topicId } a given pathname's help content lives
 * under. Tries an exact match first, then falls back to the longest mapped
 * prefix — needed for routes that carry an optional path param (e.g.
 * `/dynamic-parameter-panel/:modelId?`, whose pathname is
 * `/dynamic-parameter-panel/<id>` when a model is selected).
 */
export function getTopicForPath(pathname) {
  if (ROUTE_TOPIC_MAP[pathname]) return ROUTE_TOPIC_MAP[pathname];
  const prefixMatch = Object.keys(ROUTE_TOPIC_MAP)
    .filter((route) => route !== '/' && pathname.startsWith(`${route}/`))
    .sort((a, b) => b.length - a.length)[0];
  return prefixMatch ? ROUTE_TOPIC_MAP[prefixMatch] : DEFAULT_TOPIC;
}

/** Look up a topic's full record (with its parent section) by id pair. */
export function findTopic(sectionId, topicId) {
  const section = helpSections.find((candidate) => candidate.id === sectionId) || helpSections[0];
  const topic =
    section.topics.find((candidate) => candidate.id === topicId) || section.topics[0];
  return { section, topic };
}
